import {
    useDeleteIngredientSupplierTransport,
    useUpsertIngredientSupplierTransport,
} from "src/api/client-api/ingredientSupplierTransports/useIngredientSupplierTransports";
import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";

import type { IngredientSupplierTransportSelection } from "./ingredientSupplierTransportTypes";

export const useSaveIngredientSupplierTransports = () => {
    const upsertTransport = useUpsertIngredientSupplierTransport();
    const deleteTransport = useDeleteIngredientSupplierTransport();

    return async (ingredientSupplierIri: IRI, selections?: IngredientSupplierTransportSelection) => {
        if (selections === undefined) {
            return {};
        }

        const nextSelections: IngredientSupplierTransportSelection = { ...selections };

        try {
            await Promise.all(
                INGREDIENT_LEVEL_STAGES.flatMap((stage) => {
                    const selection = selections[stage];
                    if (selection === undefined) {
                        return [];
                    }

                    if (selection.transportCircuit === null) {
                        const transportId = selection.id;
                        if (transportId === undefined) {
                            return [];
                        }

                        return [
                            (async () => {
                                await deleteTransport.mutateAsync(transportId);
                                nextSelections[stage] = { transportCircuit: null };
                            })(),
                        ];
                    }

                    const circuitIri =
                        selection.transportCircuit["@id"] ??
                        (`/api/transport-circuits/${selection.transportCircuit.id}` as IRI);

                    return [
                        upsertTransport
                            .mutateAsync({
                                ingredientSupplier: ingredientSupplierIri,
                                stage,
                                transportCircuit: circuitIri,
                            })
                            .then((response) => {
                                nextSelections[stage] = {
                                    id: response.id,
                                    transportCircuit: selection.transportCircuit,
                                };
                            }),
                    ];
                }),
            );
        } catch (error) {
            throw new Error("Unable to save ingredient supplier transport circuits.", { cause: error });
        }

        return nextSelections;
    };
};
